import { MedicalKnowledgeEntry } from "@/services/types";

// Utility functions to extract symptoms and conditions from user answers (updated for new questionnaire)
export function extractSymptomsFromAnswers(answers: Record<string, any>): string[] {
  const symptoms: string[] = [];
  
  // Extract from symptom constellation (new structure)
  if (answers.symptom_constellation?.selections) {
    symptoms.push(...answers.symptom_constellation.selections);
  }
  
  // Extract systemic symptoms
  if (Array.isArray(answers.systemic_symptoms)) {
    symptoms.push(...answers.systemic_symptoms.filter((s: string) => s !== "None of the above"));
  }
  
  return [...new Set(symptoms.filter(Boolean))];
}

export function extractConditionsFromAnswers(answers: Record<string, any>): string[] {
  const conditions: string[] = [];
  
  // Extract category as potential condition indicator from new structure
  if (answers.symptom_constellation?.category) {
    conditions.push(answers.symptom_constellation.category);
  }
  
  return [...new Set(conditions.filter(Boolean))];
}

// AI-driven evidence-based AmIOK scoring using intelligent medical analysis
export async function calculateEvidenceBasedAmIOKScore(
  answers: Record<string, any>,
  medicalEvidence: MedicalKnowledgeEntry[] = [],
  diagnosticAnalysis?: any
): Promise<{ score: number; confidence: number; reasoning: string[] }> {
  // Import intelligent scoring service
  const { IntelligentScoringService } = await import("@/services/intelligentScoring");
  
  const symptoms = extractSymptomsFromAnswers(answers);
  
  // Use intelligent scoring for accurate medical assessment
  const intelligentResult = await IntelligentScoringService.calculateIntelligentScore(
    symptoms,
    answers,
    medicalEvidence
  );

  return {
    score: intelligentResult.score,
    confidence: intelligentResult.confidence,
    reasoning: intelligentResult.reasoning
  };
}

export function getAmIOKScoreText(score: number): string {
  if (score >= 80) return "High Priority";
  if (score >= 60) return "Elevated Risk";
  if (score >= 40) return "Moderate Risk";
  if (score >= 20) return "Minimal Concern";
  return "Very Low Risk";
}